import { FarmConfig } from "Tools/src/CandyCLI";

import { ensureArrayIsNotEmpty } from "../../../../Common/TypingUtils";
import { ConsoleApplicationContext } from "../../Commons/ConsoleApplication/ConsoleApplicationContext";
import { ExpressServerWrapper } from "../../Commons/HttpServer/ExpressWrapper/ExpressServerWrapper";
import { ILogger } from "../../Commons/Logging/ILogger";
import { FarmProxyConfig } from "../../ReadFarmConfig";
import { FormsHelperController } from "../FormsHelper/FormsHelperController";
import { ExtractTextsFromSugarController } from "../SugarExtractor/ExtractTextsFromSugarController";

import { BuildLogsWebsocketController } from "./BuildLogsWebsocketController";
import { BuildServerProxyController } from "./BuildServerProxyController";
import { BuildServerOptions, DevBuildServerOptions } from "./BuildServerServerOptions";
import { BuildServerBackendResourcesController } from "./ResourcesControllers/BuildServerBackendResourcesController";
import { BuildServerBackendResourcesUpdatesController } from "./ResourcesControllers/BuildServerBackendResourcesUpdatesController";
import { BuildServerBuildFormsController } from "./ResourcesControllers/BuildServerBuildFormsController";
import { BuildServerClientJsController } from "./ResourcesControllers/BuildServerClientJsController";
import { BuildServerClientJsFromResourcesController } from "./ResourcesControllers/BuildServerClientJsFromResourcesController";
import { BuildServerFormInfoController } from "./ResourcesControllers/BuildServerFormInfoController";
import { BuildServerLocalSourcesEngineController } from "./ResourcesControllers/BuildServerLocalSourcesEngineController";
import { BuildServerPrebuiltResourcesController } from "./ResourcesControllers/BuildServerPrebuiltResourcesController";
import { BuildServerPublishedEngineController } from "./ResourcesControllers/BuildServerPublishedEngineController";
import { BuildServerCandyWidgetController } from "./ResourcesControllers/BuildServerCandyWidgetController";

export async function runBuildServer(
    context: ConsoleApplicationContext,
    options: BuildServerOptions,
    allFarmConfig: FarmConfig[]
): Promise<void> {
    ensureArrayIsNotEmpty(allFarmConfig);
    const engineDirectory = resolveEngineDirectory(allFarmConfig);
    const candyModulesDirectory = allFarmConfig[0].candyModulesDirectory;

    const formTestingHelpersWebServer = new ExpressServerWrapper(9010, undefined, context.logger, "Forms helpers");
    formTestingHelpersWebServer.registerSigletonController(
        new FormsHelperController({ frontEndDevMode: options.developmentMode })
    );

    const webServer = new ExpressServerWrapper(9000, 9043, context.logger);
    if (!options.disableClientOverride) {
        webServer.registerSigletonController(new BuildLogsWebsocketController());
    }
    registerProxyOnServer(webServer, allFarmConfig, context.logger);

    const buildServerCandyWidgetController = new BuildServerCandyWidgetController(
        engineDirectory,
        options.buildDebugOnly,
        {
            frontEndDevMode: options.developmentMode,
        }
    );
    const buildFormsController = new BuildServerBuildFormsController(
        allFarmConfig,
        candyModulesDirectory,
        false,
        engineDirectory,
        options.buildDebugOnly,
        options.nightly
    );
    webServer.registerSigletonController(buildServerCandyWidgetController);
    webServer.registerSigletonController(buildFormsController);
    webServer.registerSigletonController(
        new BuildServerBackendResourcesController(allFarmConfig, buildFormsController)
    );

    webServer.registerSigletonController(new ExtractTextsFromSugarController(allFarmConfig));

    webServer.registerSigletonController(new BuildServerFormInfoController(allFarmConfig, false, options.nightly));

    webServer.registerSigletonController(new BuildServerPrebuiltResourcesController());
    webServer.registerSigletonController(new BuildServerPublishedEngineController(candyModulesDirectory));
    webServer.registerSigletonController(
        new BuildServerClientJsFromResourcesController(options.disableClientOverride, {
            frontEndDevMode: options.developmentMode,
        })
    );
    webServer.registerSigletonController(new BuildServerBackendResourcesUpdatesController(allFarmConfig));
    await formTestingHelpersWebServer.start();
    await webServer.start();
    await context.waitForExit();
    await webServer.stop();
    await formTestingHelpersWebServer.stop();
}

export async function runDevBuildServer(
    context: ConsoleApplicationContext,
    options: DevBuildServerOptions,
    allFarmConfig: FarmConfig[]
): Promise<void> {
    ensureArrayIsNotEmpty(allFarmConfig);
    const engineDirectory = resolveEngineDirectory(allFarmConfig);
    const candyModulesDirectory = allFarmConfig[0].candyModulesDirectory;

    const formTestingHelpersWebServer = new ExpressServerWrapper(9010, undefined, context.logger, "Forms helpers");
    formTestingHelpersWebServer.registerSigletonController(
        new FormsHelperController({ frontEndDevMode: options.developmentMode })
    );

    const webServer = new ExpressServerWrapper(9000, 9043, context.logger);
    if (!options.disableClientOverride) {
        webServer.registerSigletonController(new BuildLogsWebsocketController());
    }
    registerProxyOnServer(webServer, allFarmConfig, context.logger);
    const buildServerCandyWidgetController = new BuildServerCandyWidgetController(
        engineDirectory,
        options.buildDebugOnly,
        {
            frontEndDevMode: options.developmentMode,
        }
    );
    const buildServerBuildFormsController = new BuildServerBuildFormsController(
        allFarmConfig,
        candyModulesDirectory,
        true,
        engineDirectory,
        options.buildDebugOnly
    );
    webServer.registerSigletonController(buildServerCandyWidgetController);
    webServer.registerSigletonController(buildServerBuildFormsController);
    webServer.registerSigletonController(
        new BuildServerBackendResourcesController(allFarmConfig, buildServerBuildFormsController)
    );
    webServer.registerSigletonController(new ExtractTextsFromSugarController(allFarmConfig));
    webServer.registerSigletonController(new BuildServerFormInfoController(allFarmConfig, true, false));

    webServer.registerSigletonController(new BuildServerPrebuiltResourcesController());
    webServer.registerSigletonController(
        new BuildServerLocalSourcesEngineController(engineDirectory, options.buildDebugOnly)
    );

    webServer.registerSigletonController(
        new BuildServerClientJsController(engineDirectory, options.buildDebugOnly, options.disableClientOverride, {
            frontEndDevMode: options.developmentMode,
        })
    );
    webServer.registerSigletonController(new BuildServerBackendResourcesUpdatesController(allFarmConfig));
    await formTestingHelpersWebServer.start();
    await webServer.start();
    await context.waitForExit();
    await webServer.stop();
    await formTestingHelpersWebServer.stop();
}

const resolveEngineDirectory = (configs: FarmConfig[]): string => {
    const engineDirectory = configs.find(c => Boolean(c.engineDirectory))?.engineDirectory;
    if (!engineDirectory) {
        throw Error("All Engine directory is empty");
    }
    return engineDirectory;
};

function registerProxyOnServer(server: ExpressServerWrapper, allFarmConfig: FarmConfig[], logger: ILogger): void {
    const mergedProxy = mergeProxyConfigs(allFarmConfig);
    if (Object.keys(mergedProxy).length === 0) {
        return;
    }
    server.registerSigletonController(new BuildServerProxyController(mergedProxy, logger));
}

function mergeProxyConfigs(configs: FarmConfig[]): Record<string, FarmProxyConfig> {
    const merged: Record<string, FarmProxyConfig> = {};
    configs.forEach(fc => {
        if (fc?.proxy) {
            Object.entries(fc.proxy).forEach(([key, cfg]) => {
                merged[key] = cfg;
            });
        }
    });
    return merged;
}
